import type { ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { RecommendationCard } from '@/components/recommendations/RecommendationCard'
import { Spinner } from '@/components/ui/Spinner'
import { Icon } from '@/components/ui/Icon'
import type { DismissReason, Friend, RecommendationEntry } from '@/types'

export interface WishlistRecommendationViewEntry extends RecommendationEntry {
  wishers: Friend[]
}

export type RecommendationPendingKind =
  | 'addToWishlist'
  | 'addToPlaying'
  | 'addToPlayed'
  | 'dismiss'

export interface RecommendationPending {
  kind: RecommendationPendingKind
  appId: string
}

export interface RecommendationsViewProps {
  header?: ReactNode
  groupName?: string
  groupSize: number
  currentUserId?: string
  wishlistEntries: WishlistRecommendationViewEntry[]
  entries: RecommendationEntry[]
  generatedAt: string | null
  isLoading: boolean
  isRefreshing: boolean
  isGenerating: boolean
  pending?: RecommendationPending
  onRefresh: () => void
  onAddToWishlist: (appId: string) => void
  onAddToPlaying: (appId: string) => void
  onAddToPlayed: (appId: string) => void
  onDismiss: (appId: string, reason: DismissReason | null) => void
  onViewDetails: (appId: string) => void
  toolbarExtra?: ReactNode
  tuningSlot?: ReactNode
  canEdit?: boolean
}

const WISHLIST_VISIBLE = 4
const TOTAL_VISIBLE = 12

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return new Date(iso).toLocaleDateString()
}

export function RecommendationsView({
  header,
  groupName,
  groupSize,
  currentUserId,
  wishlistEntries,
  entries,
  generatedAt,
  isLoading,
  isRefreshing,
  isGenerating,
  pending,
  onRefresh,
  onAddToWishlist,
  onAddToPlaying,
  onAddToPlayed,
  onDismiss,
  onViewDetails,
  toolbarExtra,
  tuningSlot,
  canEdit = true,
}: RecommendationsViewProps) {
  const visibleWishlist = wishlistEntries.slice(0, WISHLIST_VISIBLE)
  const discoveryBudget = TOTAL_VISIBLE - visibleWishlist.length
  const visibleDiscovery = entries.slice(0, discoveryBudget)
  const intersectCols = visibleWishlist.length
  const besideCount = Math.max(0, WISHLIST_VISIBLE - intersectCols)

  const isPending = (kind: RecommendationPendingKind, appId: string): boolean =>
    pending?.kind === kind && pending.appId === appId

  return (
    <div className="cg-screen">
      {header}

      {tuningSlot}

      <div className="cg-toolbar">
        {!isRefreshing && generatedAt && (
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            Updated {relativeTime(generatedAt)}
          </span>
        )}
        <div className="cg-toolbar-spacer" />
        {toolbarExtra}
        {canEdit &&
          (isRefreshing ? (
            <Spinner size={16} wrapClassName="" />
          ) : (
            <button className="cg-btn" onClick={onRefresh}>
              <Icon name="refresh" size={12} /> Refresh
            </button>
          ))}
      </div>

      {isLoading && <Spinner />}

      {!isLoading && entries.length === 0 && wishlistEntries.length === 0 && !generatedAt && !isGenerating && (
        <div className="cg-rec-banner">
          <div className="cg-rec-banner-title">
            <Icon name="lightbulb" size={13} /> AI Recommendations
          </div>
          <div className="cg-rec-banner-body">
            Hit <strong>Refresh</strong> to generate personalized game picks for your group. Once
            your group has a play history, recommendations improve automatically.
          </div>
        </div>
      )}

      {!isLoading && isGenerating && entries.length === 0 && wishlistEntries.length === 0 && (
        <div className="cg-empty" style={{ color: 'var(--fg-2)' }}>
          Picking games for your group — this takes about 10–20 seconds…
        </div>
      )}

      {!isLoading && !isGenerating && entries.length === 0 && wishlistEntries.length === 0 && generatedAt && (
        <div className="cg-empty">No recommendations right now — try refreshing.</div>
      )}

      {!isLoading && (entries.length > 0 || wishlistEntries.length > 0) && (
        <div className="cg-rec-unified-grid" data-intersect-cols={intersectCols}>
          <AnimatePresence mode="popLayout">
            {intersectCols > 0 && (
              <>
                <div
                  key="frame-border"
                  className="cg-rec-frame-border"
                  style={{ gridRow: '1 / 3', gridColumn: `1 / span ${intersectCols}` }}
                  aria-hidden="true"
                />
                <div
                  key="frame-header"
                  className="cg-rec-section-header in-frame"
                  style={{ gridRow: '1', gridColumn: `1 / span ${intersectCols}` }}
                >
                  <div className="cg-rec-section-title">
                    <span className="cg-rec-section-mark">◆</span>
                    Common ground from personal wishlists
                  </div>
                  <div className="cg-rec-section-sub">
                    Games members{groupName ? <> of <strong>{groupName}</strong></> : null} have
                    already wishlisted on Steam.
                  </div>
                </div>
                {intersectCols < 4 && (
                  <div
                    key="header-row-spacer"
                    className="cg-rec-header-spacer"
                    aria-hidden="true"
                    style={{
                      gridRow: '1',
                      gridColumn: `${intersectCols + 1} / 5`,
                      height: 0,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </>
            )}

            {visibleWishlist.map((entry, idx) => {
              const isFirst = idx === 0
              const isLast = idx === visibleWishlist.length - 1
              return (
                <motion.div
                  key={`wish-${entry.appId}`}
                  className="cg-rec-frame-cell"
                  layout
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.15 } }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0.25 }}
                  style={{
                    gridRow: '2',
                    gridColumn: idx + 1,
                    marginLeft: isFirst ? 12 : 0,
                    marginRight: isLast ? 12 : 0,
                    marginBottom: 12,
                  }}
                >
                  <RecommendationCard
                    entry={entry}
                    groupSize={groupSize}
                    wishlistMeta={{ wishers: entry.wishers, currentUserId }}
                    onAddToWishlist={() => onAddToWishlist(entry.appId)}
                    onAddToPlaying={() => onAddToPlaying(entry.appId)}
                    onAddToPlayed={() => onAddToPlayed(entry.appId)}
                    onViewDetails={() => onViewDetails(entry.appId)}
                    onDismiss={(reason) => onDismiss(entry.appId, reason)}
                    isAddingToWishlist={isPending('addToWishlist', entry.appId)}
                    isAddingToPlaying={isPending('addToPlaying', entry.appId)}
                    isAddingToPlayed={isPending('addToPlayed', entry.appId)}
                    isDismissing={isPending('dismiss', entry.appId)}
                    canEdit={canEdit}
                  />
                </motion.div>
              )
            })}

            {visibleDiscovery.map((entry, i) => {
              const beside = i < besideCount && intersectCols > 0
              const style = beside
                ? { gridRow: '2', gridColumn: intersectCols + i + 1, marginBottom: 12 }
                : undefined
              return (
                <motion.div
                  key={`disc-${entry.appId}`}
                  className={beside ? 'cg-rec-beside-frame' : undefined}
                  layout
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.15 } }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0.25 }}
                  style={style}
                >
                  <RecommendationCard
                    entry={entry}
                    groupSize={groupSize}
                    onAddToWishlist={() => onAddToWishlist(entry.appId)}
                    onAddToPlaying={() => onAddToPlaying(entry.appId)}
                    onAddToPlayed={() => onAddToPlayed(entry.appId)}
                    onViewDetails={() => onViewDetails(entry.appId)}
                    onDismiss={(reason) => onDismiss(entry.appId, reason)}
                    isAddingToWishlist={isPending('addToWishlist', entry.appId)}
                    isAddingToPlaying={isPending('addToPlaying', entry.appId)}
                    isAddingToPlayed={isPending('addToPlayed', entry.appId)}
                    isDismissing={isPending('dismiss', entry.appId)}
                    canEdit={canEdit}
                  />
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
